// The board model shared by the Durable Object and the browser.
// Both sides apply the same operations so a replayed room looks identical everywhere.

/** Logical board size. Every client scales this to its own screen. */
export const BOARD_WIDTH = 1600;
export const BOARD_HEIGHT = 1000;

/** Oldest strokes are dropped past this count so a room never grows without bound. */
export const MAX_STROKES = 2000;
/** Flat [x, y, x, y, ...] coordinate count allowed in one stroke. */
export const MAX_STROKE_COORDS = 6000;

/** Strokes in this color erase the ink beneath them instead of drawing. */
export const ERASER = "#000000";

export const MIN_BRUSH = 2;
export const MAX_BRUSH = 40;

export interface Stroke {
  id: string;
  /** Stable per-tab client id. Undo removes the owner's newest stroke. */
  owner: string;
  color: string;
  size: number;
  /** Flat list of board coordinates: [x0, y0, x1, y1, ...]. */
  points: number[];
  /** False while the owner is still drawing it. */
  done: boolean;
}

export class Board {
  private order: Stroke[] = [];
  private byId = new Map<string, Stroke>();
  private evicted: string[] = [];

  constructor(strokes: Stroke[] = []) {
    this.reset(strokes);
  }

  get strokes(): readonly Stroke[] {
    return this.order;
  }

  get size(): number {
    return this.order.length;
  }

  get(id: string): Stroke | undefined {
    return this.byId.get(id);
  }

  reset(strokes: Stroke[]): void {
    this.order = [];
    this.byId.clear();
    for (const stroke of strokes) this.add(cloneStroke(stroke));
  }

  /**
   * Adds a stroke, or replaces it when the same owner sends it again
   * (a client re-sending a stroke after a reconnect).
   * Returns the stroke that is now on the board, or undefined when rejected.
   */
  add(stroke: Stroke): Stroke | undefined {
    const existing = this.byId.get(stroke.id);
    if (existing) {
      if (existing.owner !== stroke.owner) return undefined;
      existing.color = stroke.color;
      existing.size = stroke.size;
      existing.points = stroke.points.slice(0, MAX_STROKE_COORDS);
      existing.done = stroke.done;
      return existing;
    }
    const added = { ...stroke, points: stroke.points.slice(0, MAX_STROKE_COORDS) };
    this.order.push(added);
    this.byId.set(added.id, added);
    while (this.order.length > MAX_STROKES) {
      const dropped = this.order.shift();
      if (dropped) {
        this.byId.delete(dropped.id);
        this.evicted.push(dropped.id);
      }
    }
    return added;
  }

  /** Ids dropped by the MAX_STROKES cap since the last call. */
  takeEvicted(): string[] {
    const ids = this.evicted;
    this.evicted = [];
    return ids;
  }

  /** Appends points to an unfinished stroke. Only the owner may extend it. */
  append(id: string, owner: string, points: number[]): boolean {
    const stroke = this.byId.get(id);
    if (!stroke || stroke.owner !== owner || stroke.done) return false;
    const room = MAX_STROKE_COORDS - stroke.points.length;
    if (room <= 0) return false;
    const usable = points.length > room ? points.slice(0, room - (room % 2)) : points;
    if (usable.length === 0) return false;
    stroke.points.push(...usable);
    return true;
  }

  /** Marks a stroke finished. Only the owner may finish it. */
  finish(id: string, owner: string): Stroke | undefined {
    const stroke = this.byId.get(id);
    if (!stroke || stroke.owner !== owner) return undefined;
    stroke.done = true;
    return stroke;
  }

  /** Finishes every open stroke of an owner, used when their connection drops. */
  finishAllBy(owner: string): Stroke[] {
    const finished: Stroke[] = [];
    for (const stroke of this.order) {
      if (stroke.owner === owner && !stroke.done) {
        stroke.done = true;
        finished.push(stroke);
      }
    }
    return finished;
  }

  remove(id: string): boolean {
    const stroke = this.byId.get(id);
    if (!stroke) return false;
    this.byId.delete(id);
    this.order.splice(this.order.indexOf(stroke), 1);
    return true;
  }

  /** Removes the owner's newest stroke and returns its id. */
  undo(owner: string): string | undefined {
    for (let i = this.order.length - 1; i >= 0; i--) {
      const stroke = this.order[i];
      if (stroke && stroke.owner === owner) {
        this.remove(stroke.id);
        return stroke.id;
      }
    }
    return undefined;
  }

  clear(): void {
    this.order = [];
    this.byId.clear();
  }

  snapshot(): Stroke[] {
    return this.order.map(cloneStroke);
  }
}

export function cloneStroke(stroke: Stroke): Stroke {
  return { ...stroke, points: stroke.points.slice() };
}
